import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

interface SettingsFormData {
  site_name: string;
  site_description: string;
  usd_rate: string;
}

export function Settings() {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<SettingsFormData>({
    site_name: "",
    site_description: "",
    usd_rate: "0.000064", // Default value (approximately 1 USD = 15,625 IDR)
  });

  // Fetch settings
  const { data: settings } = useQuery({
    queryKey: ["settings"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("settings")
        .select("key, value");

      if (error) {
        console.error("Error fetching settings:", error);
        return {
          site_name: "Easy Bali Dashboard",
          site_description: "Manage your tours efficiently",
          usd_rate: "0.000064"
        };
      }

      // Transform array of settings to object
      const settingsObj = data.reduce((acc, curr) => {
        acc[curr.key] = curr.value;
        return acc;
      }, {} as Record<string, string>);

      setFormData({
        site_name: settingsObj.site_name || "Easy Bali Dashboard",
        site_description: settingsObj.site_description || "Manage your tours efficiently",
        usd_rate: settingsObj.usd_rate || "0.000064",
      });

      return settingsObj;
    },
  });

  // Update settings mutation
  const updateSettings = useMutation({
    mutationFn: async (data: SettingsFormData) => {
      // Convert object to array of settings
      const settingsArray = Object.entries(data).map(([key, value]) => ({
        key,
        value,
      }));

      // Update each setting individually to avoid 406 errors
      for (const setting of settingsArray) {
        const { error } = await supabase
          .from("settings")
          .update({ value: setting.value })
          .eq("key", setting.key);
          
        if (error) {
          console.error(`Error updating setting ${setting.key}:`, error);
          // If setting doesn't exist yet, try to insert it
          const { error: insertError } = await supabase
            .from("settings")
            .insert(setting);
            
          if (insertError) {
            throw insertError;
          }
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["settings"] });
      toast.success("Settings saved successfully");
      
      // Update meta tags
      document.title = formData.site_name;
      document.querySelector('meta[name="description"]')?.setAttribute("content", formData.site_description);
    },
    onError: (error) => {
      console.error("Error saving settings:", error);
      toast.error("Failed to save settings");
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateSettings.mutate(formData);
  };

  // Calculate IDR value for 1 USD based on the current rate
  const calculateIDRValue = () => {
    const rate = parseFloat(formData.usd_rate);
    if (isNaN(rate) || rate === 0) return "Invalid rate";
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(1 / rate);
  };

  return (
    <div className="flex flex-col gap-4">
      <header className="mb-8">
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <h1 className="text-3xl font-bold tracking-tight">Settings</h1>
          <p className="text-muted-foreground mt-1">
            Manage your site settings and appearance
          </p>
        </motion.div>
      </header>

      <div className="grid grid-cols-1 gap-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <form onSubmit={handleSubmit}>
            <Card className="border-border/40">
              <CardHeader>
                <CardTitle>Site Settings</CardTitle>
                <CardDescription>
                  Configure your site's basic information
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="site_name">Site Name</Label>
                    <Input
                      id="site_name"
                      value={formData.site_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, site_name: e.target.value }))}
                      placeholder="Easy Bali Dashboard"
                    />
                    <p className="text-sm text-muted-foreground">
                      This name will be displayed in the browser tab and across the application
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="site_description">Site Description</Label>
                    <Textarea
                      id="site_description"
                      value={formData.site_description}
                      onChange={(e) => setFormData(prev => ({ ...prev, site_description: e.target.value }))}
                      placeholder="Enter your site description"
                      rows={4}
                    />
                    <p className="text-sm text-muted-foreground">
                      Describe your application's purpose and functionality
                    </p>
                  </div>
                </div>
              </CardContent>
              <CardFooter className="flex justify-end">
                <Button type="submit" disabled={updateSettings.isPending}>
                  {updateSettings.isPending ? "Saving..." : "Save Changes"}
                </Button>
              </CardFooter>
            </Card>

            <Card className="border-border/40 mt-8">
              <CardHeader>
                <CardTitle>Currency Settings</CardTitle>
                <CardDescription>
                  Configure currency exchange rates for the application
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="usd_rate">USD Rate (1 IDR = x USD)</Label>
                    <Input
                      id="usd_rate"
                      type="number"
                      step="0.0000001"
                      value={formData.usd_rate}
                      onChange={(e) => setFormData(prev => ({ ...prev, usd_rate: e.target.value }))}
                      placeholder="0.000064"
                    />
                    <p className="text-sm text-muted-foreground">
                      Enter the exchange rate for 1 IDR in USD (e.g., 0.000064 means 1 IDR = 0.000064 USD)
                    </p>
                    <div className="mt-4 p-3 bg-primary/5 rounded-md">
                      <p className="text-sm font-medium">Preview: 1 USD = {calculateIDRValue()}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
              <CardFooter className="flex justify-end">
                <Button type="submit" disabled={updateSettings.isPending}>
                  {updateSettings.isPending ? "Saving..." : "Save Changes"}
                </Button>
              </CardFooter>
            </Card>
          </form>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <Card className="border-border/40 bg-amber-50">
            <CardHeader>
              <CardTitle>Note About Logo and Favicon</CardTitle>
            </CardHeader>
            <CardContent>
              <p>
                Logo and favicon settings have been removed in this version to simplify the application. 
                The application now uses a consistent icon set throughout the interface.
              </p>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}